import type { FlexibleConnectedPositionStrategyOrigin } from '@angular/cdk/overlay';
import type { PopoverRef } from './popover-ref';

export interface PopoverOwnership {
    readonly children: Set<PopoverRef>;
    readonly origin: FlexibleConnectedPositionStrategyOrigin;
    overlayElement?: HTMLElement;
    pointerBridge?: HTMLElement;
}

class PopoverOwnershipRegistry {
    private readonly entries = new WeakMap<PopoverRef, PopoverOwnership>();

    public get(ref: PopoverRef): PopoverOwnership | undefined {
        return this.entries.get(ref);
    }

    public set(ref: PopoverRef, ownership: PopoverOwnership): void {
        this.entries.set(ref, ownership);
    }

    public delete(ref: PopoverRef): boolean {
        return this.entries.delete(ref);
    }
}

export const popoverOwnership = new PopoverOwnershipRegistry();

export function popoverContainsTarget(
    ref: PopoverRef,
    target: EventTarget | null
): boolean {
    if (!(target instanceof Node)) return false;
    const ownership = popoverOwnership.get(ref);
    return (
        ownership != null &&
        (containsOriginTarget(ownership.origin, target) ||
            ownership.overlayElement?.contains(target) === true ||
            ownership.pointerBridge?.contains(target) === true ||
            Array.from(ownership.children).some((child) =>
                popoverContainsTarget(child, target)
            ))
    );
}

export function setPopoverPointerBridge(
    ref: PopoverRef,
    bridge: HTMLElement | null
): void {
    const ownership = popoverOwnership.get(ref);
    if (ownership) ownership.pointerBridge = bridge ?? undefined;
}

function containsOriginTarget(
    origin: FlexibleConnectedPositionStrategyOrigin,
    target: Node
): boolean {
    if (origin instanceof Element) {
        return origin === target || origin.contains(target);
    }

    if (
        typeof origin === 'object' &&
        origin !== null &&
        'elementRef' in origin
    ) {
        const originElement = (
            origin as {
                elementRef?: {
                    nativeElement?: unknown;
                };
            }
        ).elementRef?.nativeElement;
        return (
            originElement instanceof Element &&
            (originElement === target || originElement.contains(target))
        );
    }

    return false;
}
